/**
 * Keyboard Shortcuts directory modal.
 *
 * Renders the single-source-of-truth catalog (keyboardShortcuts.ts) as a grouped, platform-aware
 * cheatsheet. Opened from the canvas dock (CanvasToolbox). Frontend-only chrome — no YDoc/backend.
 *
 * Requested by Rowell Tan (Workplace wish 1561801082243856): centralize discoverable shortcuts.
 */

import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../ui/Modal'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'
import {
  SHORTCUT_GROUPS,
  isMacPlatform,
  resolveCombo,
  type Combo,
  type ShortcutGroup,
} from './keyboardShortcuts'

const shortcutsLog = createNadDebug('dw:keyboard-shortcuts:modal')

interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
}

function KeyChip({ label }: { label: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex min-w-[1.5rem] items-center justify-center rounded border border-border',
        'bg-surface-2 px-1.5 py-0.5 font-mono text-xs font-medium leading-none text-foreground/90',
      )}
    >
      {label}
    </kbd>
  )
}

function ComboChips({ combos }: { combos: Combo[] }) {
  const { t } = useTranslation('canvas')
  const isMac = useMemo(() => isMacPlatform(), [])
  return (
    <span className="flex flex-wrap items-center justify-end gap-1">
      {combos.map((combo, ci) => (
        <span key={ci} className="flex items-center gap-1">
          {ci > 0 && (
            <span className="px-0.5 text-xxs text-muted-foreground">
              {t('keyboardShortcuts.or')}
            </span>
          )}
          {resolveCombo(combo, isMac).map((chip, ki) => (
            <span key={ki} className="flex items-center gap-1">
              {ki > 0 && <span className="text-xxs text-muted-foreground">+</span>}
              <KeyChip label={chip} />
            </span>
          ))}
        </span>
      ))}
    </span>
  )
}

function GroupColumn({ group }: { group: ShortcutGroup }) {
  const { t } = useTranslation('canvas')
  return (
    <div className="mb-5 break-inside-avoid" data-testid={`shortcuts-group-${group.scope}`}>
      <div className="mb-2 text-xxs font-medium uppercase tracking-wider text-muted-foreground">
        {t(group.titleKey)}
      </div>
      <div className="flex flex-col">
        {group.bindings.map((b) => (
          <div
            key={b.id}
            className="flex items-center justify-between gap-4 py-1.5"
            data-testid={`shortcut-${b.id}`}
          >
            <span className="text-sm text-foreground/90">{t(b.labelKey)}</span>
            <ComboChips combos={b.combos} />
          </div>
        ))}
      </div>
    </div>
  )
}

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const { t } = useTranslation('canvas')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    shortcutsLog({
      event: 'keyboard-shortcuts-modal-opened',
      trace_id: 'keyboard-shortcuts:open',
      group_count: SHORTCUT_GROUPS.length,
      binding_count: SHORTCUT_GROUPS.reduce((n, g) => n + g.bindings.length, 0),
    })
  }, [isOpen])

  // Modal-open shortcut isolation. This modal has no text input, so while it is open a keydown
  // must NOT reach the app's global shortcut handlers and delete/duplicate/paste nodes behind it.
  // A WINDOW capture-phase listener is the only reliable guard: it fires before every other
  // capture listener (e.g. GroupNode / TimelineEditorModal on document) AND before the bubble-phase
  // window/document handlers (WorkspacePageV2, Modal), regardless of where focus currently sits
  // (toolbar button, close button, or modal content). We stop everything except Escape/Tab, which
  // Modal.tsx owns for close + focus-trap. We never preventDefault, so button activation is intact.
  useEffect(() => {
    if (!isOpen) return
    const guard = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') return
      e.stopPropagation()
      e.stopImmediatePropagation()
    }
    window.addEventListener('keydown', guard, true)
    return () => window.removeEventListener('keydown', guard, true)
  }, [isOpen])

  // Move focus into the dialog on open so Escape/Tab focus-trap work even if the opening dock
  // button retained focus.
  useEffect(() => {
    if (!isOpen) return
    const id = requestAnimationFrame(() => containerRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('keyboardShortcuts.title')} size="lg">
      <div ref={containerRef} tabIndex={-1} className="outline-none" data-testid="keyboard-shortcuts-modal">
        <div className="columns-1 gap-8 md:columns-2">
          {SHORTCUT_GROUPS.map((group) => (
            <GroupColumn key={group.scope} group={group} />
          ))}
        </div>
      </div>
    </Modal>
  )
}
